import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { urls } from '../../../utils/url.utils';

export type NotificationPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export interface PushNotificationPayload {
  title?: string;
  body?: string;
  icon?: string;
  badge?: string;
  url?: string;
  tripId?: string;
  type?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebPushService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  public isSubscribed$ = new BehaviorSubject<boolean>(false);
  public permissionStatus$ = new BehaviorSubject<NotificationPermissionState>('default');
  public isSupported$ = new BehaviorSubject<boolean>(false);
  public isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {
    this.checkSupport();
  }

  /**
   * Check if the browser supports Service Workers, PushManager, and Notification API
   */
  public checkSupport(): boolean {
    const supported =
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    this.isSupported$.next(supported);

    if (supported) {
      this.permissionStatus$.next(Notification.permission as NotificationPermissionState);
    } else {
      this.permissionStatus$.next('unsupported');
    }

    return supported;
  }

  /**
   * Register the Service Worker (runs on app bootstrap)
   */
  public async initServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!this.checkSupport()) {
      console.warn('⚠️ Push notifications are not supported in this browser environment.');
      return null;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('✅ Web Push Service Worker registered with scope:', this.swRegistration.scope);

      // Listen for messages posted from service worker (e.g. notification click)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'PUSH_NOTIFICATION_CLICKED') {
          console.log('🔔 Notification clicked:', event.data);
          this.handleNotificationClick(event.data);
        }
      });

      // Synchronize current subscription state
      await this.syncSubscriptionStatus();

      return this.swRegistration;
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Sync browser push subscription state with backend
   */
  public async syncSubscriptionStatus(): Promise<boolean> {
    if (!this.checkSupport()) {
      this.ngZone.run(() => this.isSubscribed$.next(false));
      return false;
    }

    try {
      if (!this.swRegistration && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        this.swRegistration = (await navigator.serviceWorker.getRegistration()) || null;
      }

      this.ngZone.run(() => {
        this.permissionStatus$.next(Notification.permission as NotificationPermissionState);
      });

      if (!this.swRegistration) {
        this.ngZone.run(() => this.isSubscribed$.next(false));
        return false;
      }

      const subscription = await this.swRegistration.pushManager.getSubscription();
      const hasSubscription = !!subscription;
      this.ngZone.run(() => this.isSubscribed$.next(hasSubscription));
      return hasSubscription;
    } catch (e) {
      this.ngZone.run(() => this.isSubscribed$.next(false));
      return false;
    }
  }

  /**
   * Request permission and subscribe to Web Push upon explicit user interaction
   */
  public async requestPermissionAndSubscribe(): Promise<{ success: boolean; message: string }> {
    if (!this.checkSupport()) {
      return { success: false, message: 'Push notifications are not supported in this browser.' };
    }

    this.ngZone.run(() => this.isLoading$.next(true));

    try {
      // 1. Explicitly request permission
      const permission = await Notification.requestPermission();
      this.ngZone.run(() => {
        this.permissionStatus$.next(permission as NotificationPermissionState);
      });

      if (permission !== 'granted') {
        this.ngZone.run(() => this.isLoading$.next(false));
        if (permission === 'denied') {
          return {
            success: false,
            message: 'Notification permission was denied. Please allow notifications in your browser settings.',
          };
        }
        return { success: false, message: 'Notification permission was not granted.' };
      }

      // 2. Ensure Service Worker registration is active
      if (!this.swRegistration) {
        try {
          this.swRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        } catch {
          this.swRegistration = await navigator.serviceWorker.ready;
        }
      }
      if (!this.swRegistration) {
        this.swRegistration = await navigator.serviceWorker.ready;
      }

      // 3. Get VAPID Public Key from backend
      const publicKeyResponse = await this.http
        .get<{ publicKey: string }>(urls.API_URL + urls.PUSH_PUBLIC_KEY)
        .toPromise();

      if (!publicKeyResponse || !publicKeyResponse.publicKey) {
        throw new Error('Could not retrieve VAPID Public Key from server.');
      }

      const applicationServerKey = this.urlBase64ToUint8Array(publicKeyResponse.publicKey);

      // 4. Subscribe with PushManager (clear existing if present)
      try {
        const existingSub = await this.swRegistration.pushManager.getSubscription();
        if (existingSub) {
          console.log('🔄 Replacing existing push subscription...');
          await existingSub.unsubscribe().catch(() => {});
        }
      } catch (e) {
        console.warn('Existing subscription check warning:', e);
      }

      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // 5. Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      const payload = {
        endpoint: subscription.endpoint,
        expirationTime: subscription.expirationTime,
        keys: subscriptionJson.keys,
      };

      await this.http.post(urls.API_URL + urls.PUSH_SUBSCRIBE, payload).toPromise();

      this.ngZone.run(() => {
        this.isSubscribed$.next(true);
        this.isLoading$.next(false);
      });

      return { success: true, message: 'Push notifications enabled successfully!' };
    } catch (error: any) {
      this.ngZone.run(() => this.isLoading$.next(false));
      console.error('❌ Failed to subscribe for push notifications:', error);
      return {
        success: false,
        message: error?.message || 'Failed to enable push notifications.',
      };
    }
  }

  /**
   * Unsubscribe from Web Push notifications
   */
  public async unsubscribe(): Promise<{ success: boolean; message: string }> {
    this.ngZone.run(() => this.isLoading$.next(true));

    try {
      if (this.swRegistration) {
        const subscription = await this.swRegistration.pushManager.getSubscription();

        if (subscription) {
          // 1. Notify backend to remove subscription
          await this.http
            .post(urls.API_URL + urls.PUSH_UNSUBSCRIBE, { endpoint: subscription.endpoint })
            .toPromise()
            .catch((err) => console.warn('Backend unregister notice failed:', err));

          // 2. Unsubscribe browser PushManager
          await subscription.unsubscribe();
        }
      }

      this.ngZone.run(() => {
        this.isSubscribed$.next(false);
        this.isLoading$.next(false);
      });
      return { success: true, message: 'Push notifications disabled.' };
    } catch (error: any) {
      this.ngZone.run(() => this.isLoading$.next(false));
      console.error('❌ Failed to unsubscribe:', error);
      return { success: false, message: error?.message || 'Failed to disable notifications.' };
    }
  }

  /**
   * Trigger a test notification from backend
   */
  public sendTestNotification(customPayload?: PushNotificationPayload): Observable<any> {
    const payload = customPayload || {
      title: 'Chrome Desktop Notifications Active',
      body: 'Real-time Web Push is working properly on this device.',
      icon: '/assets/icons/notification-icon.png',
      badge: '/assets/icons/badge.png',
      url: '/',
      type: 'test_notification',
    };

    return this.http.post(urls.API_URL + urls.PUSH_TEST, payload);
  }

  /**
   * Helper: Handle notification click navigation or event
   */
  private handleNotificationClick(eventData: any) {
    if (eventData && eventData.targetUrl) {
      // In-app navigation if needed
      console.log('Navigating to:', eventData.targetUrl);
    }
  }

  /**
   * Helper: Convert URL-safe base64 string to Uint8Array for PushManager
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
